import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { baseUrl } from "../Univ.js";

import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";

// =========================================================
// HELPERS
// =========================================================

const toNumber = (value) => {
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? number : 0;
};

const money = (value) =>
    toNumber(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const normalize = (value) =>
    String(value ?? "").trim().toLowerCase();

const formatDate = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const getArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    return [];
};

// =========================================================
// DEPARTMENT NAME
// =========================================================

const getSaleDepartment = (item, departments) => {
    const rawSaleType = item.sale_type;

    if (
        rawSaleType === undefined ||
        rawSaleType === null ||
        rawSaleType === ""
    ) {
        return "Unassigned";
    }

    const saleType = String(rawSaleType).trim();

    const found = departments.find(
        (department) =>
            String(department.id) === saleType ||
            normalize(department.department) === normalize(saleType)
    );

    return found?.department || saleType;
};

// =========================================================
// CONSULTANT
// =========================================================

const getConsultant = (item) =>
    item.consultant_name ??
    item.consultant ??
    item.cons__consultant ??
    item.cons__name ??
    item.doctor_name ??
    item.doctor ??
    item.cons_name ??
    "";

// =========================================================
// INVOICE LINK
// =========================================================

const getInvoiceLink = (item) => {
    if (item.sale_type === "IndentBill") {
        return `/pharm-invoice-indent/${item.sale_id}`;
    }

    return `/pharm-invoice-other/${item.sale_id}`;
};

// =========================================================
// CASHIER NAME
// =========================================================

const getUserName = (user) =>
    user.username ??
    user.user_name ??
    user.name ??
    user.first_name ??
    user.email ??
    "";

// =========================================================
// SUMMARY CARD
// =========================================================

const SummaryCard = ({ title, value, color = "#1976d2" }) => (
    <Card
        elevation={2}
        sx={{
            height: "100%",
            borderLeft: `4px solid ${color}`,
        }}
    >
        <CardContent sx={{ py: 2 }}>
            <Typography
                variant="body2"
                color="text.secondary"
            >
                {title}
            </Typography>

            <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mt: 0.5 }}
            >
                {value}
            </Typography>
        </CardContent>
    </Card>
);

// =========================================================
// MAIN COMPONENT
// =========================================================

function PharmCollectionAll() {
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get("type");

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [addedby, setAddedby] = useState("");
    const [mode, setMode] = useState("");

    const [consultant, setConsultant] = useState("all");
    const [department, setDepartment] = useState("all");

    const [rows, setRows] = useState([]);
    const [userData, setUserData] = useState([]);
    const [departments, setDepartments] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // =====================================================
    // LOAD CASHIERS AND DEPARTMENTS
    // =====================================================

    useEffect(() => {
        let cancelled = false;

        const loadDropdowns = async () => {
            try {
                const [
                    userResponse,
                    departmentResponse,
                ] = await Promise.all([
                    axios.get(`${baseUrl}user-search/`),
                    axios.get(`${baseUrl}pharmacy-departments/`),
                ]);

                if (cancelled) return;

                setUserData(getArray(userResponse.data));

                const departmentData = getArray(
                    departmentResponse.data
                );

                setDepartments(
                    departmentData
                        .map((item) => ({
                            id: item.id,
                            department:
                                item.department ??
                                item.name ??
                                item.label ??
                                "",
                        }))
                        .filter((item) => item.department)
                );
            } catch (err) {
                console.error(
                    "Dropdown loading error:",
                    err
                );
            }
        };

        loadDropdowns();

        return () => {
            cancelled = true;
        };
    }, []);

    // =====================================================
    // FETCH PHARMACY COLLECTION
    // =====================================================

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError("");

        try {
            const params = {
                start_date: formatDate(startDate),
                end_date: formatDate(endDate),
                addedby,
                mode,
                status: 1,
            };

            const response = await axios.get(
                `${baseUrl}all-pharm-sale/`,
                { params }
            );

            const data = getArray(response.data);

            // Exclude Indent mode records.
            const filteredData = data.filter(
                (item) => item.mode !== "Indent"
            );

            const formattedRows = filteredData.map(
                (item, index) => {
                    const saleDepartment =
                        getSaleDepartment(item, departments);

                    const saleConsultant =
                        getConsultant(item);

                    return {
                        ...item,

                        id: item.id ?? index,

                        // Department name is displayed
                        // in the Sale Type column.
                        sale_type: saleDepartment,

                        // Used by the department filter
                        // and department summary.
                        department: saleDepartment,

                        consultant: saleConsultant,

                        invoiceLink: getInvoiceLink(item),

                        total: toNumber(item.total),
                        paid: toNumber(item.paid),
                        balance: toNumber(item.balance),
                        discount: toNumber(item.discount),
                    };
                }
            );

            setRows(formattedRows);
        } catch (err) {
            console.error(
                "Pharmacy collection fetch error:",
                err
            );

            setRows([]);

            setError(
                err.response?.data?.detail ||
                    err.response?.data?.error ||
                    "Unable to load pharmacy collection report."
            );
        } finally {
            setIsLoading(false);
        }
    }, [
        startDate,
        endDate,
        addedby,
        mode,
        departments,
    ]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // =====================================================
    // CONSULTANT OPTIONS
    // =====================================================

    const consultants = useMemo(() => {
        const names = rows
            .map((row) => row.consultant)
            .filter(Boolean);

        return [...new Set(names)].sort((a, b) =>
            String(a).localeCompare(String(b))
        );
    }, [rows]);

    // =====================================================
    // DEPARTMENT OPTIONS
    // =====================================================

    const departmentOptions = useMemo(() => {
        const names = [
            ...departments.map((item) => item.department),
            ...rows.map((row) => row.department),
        ].filter(Boolean);

        return [...new Set(names)].sort((a, b) =>
            String(a).localeCompare(String(b))
        );
    }, [departments, rows]);

    // =====================================================
    // FILTER ROWS
    // =====================================================

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            const consultantMatch =
                consultant === "all" ||
                normalize(row.consultant) ===
                    normalize(consultant);

            const departmentMatch =
                department === "all" ||
                normalize(row.department) ===
                    normalize(department);

            return consultantMatch && departmentMatch;
        });
    }, [rows, consultant, department]);

    // =====================================================
    // OVERALL TOTALS
    // =====================================================

    const totals = useMemo(() => {
        const result = {
            total: 0,
            paid: 0,
            balance: 0,
            discount: 0,
            count: filteredRows.length,
        };

        filteredRows.forEach((row) => {
            result.total += toNumber(row.total);
            result.paid += toNumber(row.paid);
            result.balance += toNumber(row.balance);
            result.discount += toNumber(row.discount);
        });

        return result;
    }, [filteredRows]);

    // =====================================================
    // DEPARTMENT TOTALS
    // =====================================================

    const departmentTotals = useMemo(() => {
        const result = {};

        filteredRows.forEach((row) => {
            const name = row.department || "Unassigned";

            if (!result[name]) {
                result[name] = {
                    total: 0,
                    paid: 0,
                    balance: 0,
                    discount: 0,
                    count: 0,
                };
            }

            result[name].total += toNumber(row.total);
            result[name].paid += toNumber(row.paid);
            result[name].balance += toNumber(row.balance);
            result[name].discount += toNumber(row.discount);
            result[name].count += 1;
        });

        return result;
    }, [filteredRows]);

    // =====================================================
    // PAYMENT MODE TOTALS
    // =====================================================

    const paymentModeTotals = useMemo(() => {
        const result = {
            cash: 0,
            card: 0,
            UPI: 0,
            advance: 0,
            creditpay: 0,
            other: 0,
        };

        filteredRows.forEach((row) => {
            const paymentMode = normalize(row.mode);
            const amount = toNumber(row.paid);

            if (paymentMode === "cash") {
                result.cash += amount;
            } else if (paymentMode === "card") {
                result.card += amount;
            } else if (paymentMode === "upi") {
                result.UPI += amount;
            } else if (paymentMode === "advance") {
                result.advance += amount;
            } else if (paymentMode === "creditpay") {
                result.creditpay += amount;
            } else {
                result.other += amount;
            }
        });

        return result;
    }, [filteredRows]);

    // =====================================================
    // DATAGRID COLUMNS
    // =====================================================

    const columns = useMemo(() => {
        const baseColumns = [
            {
                field: "sno",
                headerName: "S.No",
                width: 65,
                sortable: false,
                filterable: false,
                renderCell: (params) =>
                    params.api.getRowIndexRelativeToVisibleRows(
                        params.id
                    ) + 1,
            },
            {
                field: "add_date_date",
                headerName: "Date",
                width: 115,
            },
            {
                field: "add_time",
                headerName: "Time",
                width: 100,
            },
            {
                field: "pat__pat_id",
                headerName: "Patient ID",
                width: 110,
            },
            {
                field: "pat__patient",
                headerName: "Patient",
                width: 180,
            },
            {
                field: "sale_type",
                headerName: "Sale Type",
                width: 140,
            },
            {
                field: "consultant",
                headerName: "Consultant",
                width: 140,
            },
            {
                field: "mode",
                headerName: "Payment Mode",
                width: 120,
            },
            {
                field: "sale_id",
                headerName: "Invoice No",
                width: 145,
                renderCell: (params) => (
                    <Link
                        to={params.row.invoiceLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {params.row.sale_id}
                    </Link>
                ),
            },
            {
                field: "total",
                headerName: "Invoiced Amount",
                width: 130,
                type: "number",
                valueFormatter: (value) => money(value),
            },
            {
                field: "discount",
                headerName: "Discount",
                width: 110,
                type: "number",
                valueFormatter: (value) => money(value),
            },
            {
                field: "paid",
                headerName: "Paid Amount",
                width: 120,
                type: "number",
                valueFormatter: (value) => money(value),
            },
            {
                field: "balance",
                headerName: "Balance",
                width: 110,
                type: "number",
                valueFormatter: (value) => money(value),
            },
            {
                field: "addedby",
                headerName: "Cashier",
                width: 120,
            },
        ];

        if (type === "salesreturn") {
            baseColumns.splice(9, 0, {
                field: "return",
                headerName: "Return",
                width: 110,
                sortable: false,
                renderCell: (params) => (
                    <Link
                        to={`/sales-return/${params.row.sale_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-warning"
                    >
                        Return
                    </Link>
                ),
            });
        }

        return baseColumns;
    }, [type]);

    // =====================================================
    // CLEAR FILTERS
    // =====================================================

    const clearFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setAddedby("");
        setMode("");
        setConsultant("all");
        setDepartment("all");
    };

    // =====================================================
    // UI
    // =====================================================

    return (
        <Box sx={{ p: 2 }}>
            {/* PAGE TITLE */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 2,
                }}
            >
                <Typography variant="h5" fontWeight={700}>
                    Pharmacy Collection Report
                </Typography>

                <Chip
                    label={`${filteredRows.length} Records`}
                    color="primary"
                    variant="outlined"
                />
            </Box>

            {/* FILTERS */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                From Date
                            </Typography>

                            <DatePicker
                                selected={startDate}
                                onChange={setStartDate}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="Start Date"
                                className="form-control"
                                isClearable
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                To Date
                            </Typography>

                            <DatePicker
                                selected={endDate}
                                onChange={setEndDate}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="End Date"
                                className="form-control"
                                minDate={startDate || undefined}
                                isClearable
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Cashier</InputLabel>

                                <Select
                                    value={addedby}
                                    label="Cashier"
                                    onChange={(e) =>
                                        setAddedby(e.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        All Cashiers
                                    </MenuItem>

                                    {userData.map((user) => {
                                        const name = getUserName(user);

                                        return (
                                            <MenuItem
                                                key={user.id ?? name}
                                                value={name}
                                            >
                                                {name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Payment Mode</InputLabel>

                                <Select
                                    value={mode}
                                    label="Payment Mode"
                                    onChange={(e) =>
                                        setMode(e.target.value)
                                    }
                                >
                                    <MenuItem value="">
                                        All Modes
                                    </MenuItem>
                                    <MenuItem value="Cash">Cash</MenuItem>
                                    <MenuItem value="Card">Card</MenuItem>
                                    <MenuItem value="UPI">UPI</MenuItem>
                                    <MenuItem value="Advance">Advance</MenuItem>
                                    <MenuItem value="CreditPay">Credit Pay</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Consultant</InputLabel>

                                <Select
                                    value={consultant}
                                    label="Consultant"
                                    onChange={(e) =>
                                        setConsultant(e.target.value)
                                    }
                                >
                                    <MenuItem value="all">
                                        All Consultants
                                    </MenuItem>

                                    {consultants.map((name) => (
                                        <MenuItem
                                            key={name}
                                            value={name}
                                        >
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Department</InputLabel>

                                <Select
                                    value={department}
                                    label="Department"
                                    onChange={(e) =>
                                        setDepartment(e.target.value)
                                    }
                                >
                                    <MenuItem value="all">
                                        All Departments
                                    </MenuItem>

                                    {departmentOptions.map((name) => (
                                        <MenuItem
                                            key={name}
                                            value={name}
                                        >
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                            }}
                        >
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={clearFilters}
                            >
                                Clear Filters
                            </Button>

                            <Button
                                variant="contained"
                                onClick={fetchData}
                                disabled={isLoading}
                            >
                                Refresh
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* ERROR */}
            {error && (
                <Typography
                    color="error"
                    sx={{ mb: 2 }}
                >
                    {error}
                </Typography>
            )}

            {/* SUMMARY CARDS */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2}>
                    <SummaryCard
                        title="Total Invoiced"
                        value={`₹ ${money(totals.total)}`}
                        color="#1976d2"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <SummaryCard
                        title="Total Paid"
                        value={`₹ ${money(totals.paid)}`}
                        color="#2e7d32"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <SummaryCard
                        title="Total Balance"
                        value={`₹ ${money(totals.balance)}`}
                        color="#ed6c02"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <SummaryCard
                        title="Total Discount"
                        value={`₹ ${money(totals.discount)}`}
                        color="#9c27b0"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <SummaryCard
                        title="Total Bills"
                        value={totals.count}
                        color="#455a64"
                    />
                </Grid>
            </Grid>

            {/* PAYMENT MODE SUMMARY */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ mb: 1 }}
                    >
                        Payment Mode Summary
                    </Typography>

                    <Grid container spacing={2}>
                        {[
                            ["Cash", paymentModeTotals.cash],
                            ["Card", paymentModeTotals.card],
                            ["UPI", paymentModeTotals.UPI],
                            ["Advance", paymentModeTotals.advance],
                            ["Credit Pay", paymentModeTotals.creditpay],
                            ["Other", paymentModeTotals.other],
                        ].map(([label, value]) => (
                            <Grid item xs={6} sm={4} md={2} key={label}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {label}
                                </Typography>

                                <Typography fontWeight={700}>
                                    ₹ {money(value)}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* DEPARTMENT SUMMARY */}
            {Object.keys(departmentTotals).length > 0 && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ mb: 1 }}
                        >
                            Department Summary
                        </Typography>

                        <Grid container spacing={2}>
                            {Object.entries(departmentTotals).map(
                                ([name, values]) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        key={name}
                                    >
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                border: "1px solid #e0e0e0",
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Typography
                                                fontWeight={700}
                                            >
                                                {name}
                                            </Typography>

                                            <Typography variant="body2">
                                                Bills: {values.count}
                                            </Typography>

                                            <Typography variant="body2">
                                                Total: ₹ {money(values.total)}
                                            </Typography>

                                            <Typography variant="body2">
                                                Paid: ₹ {money(values.paid)}
                                            </Typography>

                                            <Typography variant="body2">
                                                Balance: ₹ {money(values.balance)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* DATA GRID */}
            <Card>
                <Box
                    sx={{
                        height: 620,
                        width: "100%",
                        p: 1,
                    }}
                >
                    {isLoading ? (
                        <Box
                            sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                            }}
                        >
                            <CircularProgress size={28} />
                            <Typography>
                                Loading pharmacy collection...
                            </Typography>
                        </Box>
                    ) : (
                        <DataGrid
                            rows={filteredRows}
                            columns={columns}
                            getRowId={(row) => row.id}
                            slots={{
                                toolbar: GridToolbar,
                            }}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 25,
                                        page: 0,
                                    },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            disableRowSelectionOnClick
                            density="compact"
                            sx={{
                                border: 0,
                                "& .MuiDataGrid-columnHeaders": {
                                    backgroundColor: "#f5f5f5",
                                    fontWeight: 700,
                                },
                                "& .MuiDataGrid-cell": {
                                    fontSize: "13px",
                                },
                            }}
                        />
                    )}
                </Box>
            </Card>
        </Box>
    );
}

export default PharmCollectionAll;